// app/auth/components/authProvider.tsx
'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useActivationNewsletterTrigger } from '@/app/features/activation/hooks/useActivationNewsletterTrigger';
import { PostHogUserIdentifier } from '@/app/shared/providers/PostHogProvider';

import { authService } from '../services_and_hooks/authService';
import { useAuthStore } from '../services_and_hooks/authStore';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Automatically check and trigger welcome newsletter when user completes activation
  useActivationNewsletterTrigger();
  const queryClient = useQueryClient();
  const authListenerRef = useRef<any>(null);
  const isInitializedRef = useRef(false);
  const lastEventRef = useRef<string>('');
  const { reset, setSession } = useAuthStore();

  useEffect(() => {
    if (isInitializedRef.current) {
      console.log('[AuthProvider] ⚠️ Already initialized, skipping...');
      return;
    }

    console.log('[AuthProvider] 🔧 Setting up auth listener...');
    isInitializedRef.current = true;
    
    const { data: authListener } = authService.onAuthStateChange(
      async (event, newSession) => {
        // Prevent duplicate events
        const eventKey = `${event}-${!!newSession?.user}-${newSession?.user?.id || 'none'}`;
        if (lastEventRef.current === eventKey) {
          console.log(`[AuthProvider] 🔕 Duplicate event ignored: ${event}`);
          return;
        }
        lastEventRef.current = eventKey;
        
        console.log(`[AuthProvider] 🔔 Auth event: ${event}`, {
          hasUser: !!newSession?.user,
          userId: newSession?.user?.id,
          hasAccessToken: !!newSession?.access_token,
        });
        
        if (event === 'SIGNED_OUT') {
          console.log('[AuthProvider] 👋 User signed out - clearing everything');
          reset();
          queryClient.clear();
        } else if (event === 'SIGNED_IN') {
          console.log('[AuthProvider] 🔑 User signed in - updating session immediately');
          // Update session immediately in store
          if (newSession) {
            setSession(newSession);
          }
          // Then invalidate queries
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ['auth-session'] });
          }, 50);
        } else if (event === 'TOKEN_REFRESHED') {
          console.log('[AuthProvider] 🔄 Token refreshed - updating session');
          if (newSession) {
            setSession(newSession);
          }
          queryClient.invalidateQueries({ queryKey: ['auth-session'] });
        } else if (event === 'INITIAL_SESSION') {
          console.log('[AuthProvider] 🚀 Initial session detected');
          // For initial session, update store immediately
          if (newSession) {
            setSession(newSession);
          }
          // Then refresh queries
          queryClient.invalidateQueries({ queryKey: ['auth-session'] });
        }
      }
    );
    
    authListenerRef.current = authListener;
    
    return () => {
      console.log('[AuthProvider] 🧹 Cleanup...');
      if (authListenerRef.current) {
        authListenerRef.current.subscription.unsubscribe();
        authListenerRef.current = null;
      }
      isInitializedRef.current = false;
      lastEventRef.current = '';
    };
  }, [queryClient, reset, setSession]);

  return (
    <>
      <PostHogUserIdentifier />
      {children}
    </>
  );
}
// // app/auth/components/authProvider.tsx
// 'use client';

// import { useQueryClient } from '@tanstack/react-query';
// import { useEffect, useRef } from 'react';

// import { authService } from '../services_and_hooks/authService';
// import { useAuthStore } from '../services_and_hooks/authStore';

// export function AuthProvider({ children }: { children: React.ReactNode }) {
//   const queryClient = useQueryClient();
//   const authListenerRef = useRef<any>(null);
//   const lastProcessedUserIdRef = useRef<string | null>(null);
//   const isInitializedRef = useRef(false);

//   // Set up the SINGLE global auth listener
//   useEffect(() => {
//     if (isInitializedRef.current) {
//       return;
//     }

//     console.log('[AuthProvider] 🔧 Setting up SINGLE global auth listener...');
//     isInitializedRef.current = true;
    
//     const { data: authListener } = authService.onAuthStateChange(
//       async (event, newSession) => {
//         console.log(`[AuthProvider] 🔔 Auth event: ${event}`);
        
//         if (event === 'SIGNED_OUT') {
//           console.log('[AuthProvider] 👋 User signed out, clearing everything...');
//           lastProcessedUserIdRef.current = null;
//           useAuthStore.getState().reset();
//           queryClient.clear();
//         } else if (event === 'SIGNED_IN' && newSession?.user) {
//           const newUserId = newSession.user.id;
          
//           if (lastProcessedUserIdRef.current === newUserId) {
//             console.log('[AuthProvider] 🔄 Ignoring duplicate SIGNED_IN for same user');
//             return;
//           }
          
//           console.log('[AuthProvider] 🎯 New user signed in, setting session...');
//           lastProcessedUserIdRef.current = newUserId;
//           useAuthStore.getState().setSession(newSession);
          
//           // Let useAuthHook handle the data fetching via React Query
//           queryClient.invalidateQueries({ queryKey: ['session-and-user-data'] });
//         }
//       }
//     );
    
//     authListenerRef.current = authListener;
    
//     return () => {
//       console.log('[AuthProvider] 🧹 Cleaning up auth listener...');
//       if (authListenerRef.current) {
//         authListenerRef.current.subscription.unsubscribe();
//         authListenerRef.current = null;
//       }
//       isInitializedRef.current = false;
//       lastProcessedUserIdRef.current = null;
//     };
//   }, [queryClient]);

//   return <>{children}</>;
// }